import {
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  repeatWhen,
  switchMap,
  take,
} from 'rxjs/operators';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HeaderService } from '@app/service/header/header.service';
import { StateService } from 'src/app/service/state/state.service';
import {
  StandardCheckoutResponse,
  UserService,
} from 'src/app/service/user-service/user.service';
import { RouteComponent } from '../../route.component';
import {
  Market,
  PaymentMethodVM,
} from '@app/model/payment-request/payment-request.dto';
import { SelectorItem } from '@app/component/selector/selector.component';
import { ValuePipe } from '@app/shared/pipe/value.pipe';
import { LoanOption } from '@app/route/shared/LoanOption';
import { DeLoanOptionService } from '@app/service/de-loan-option/de-loan-option.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-de-register-iban',
  templateUrl: './de-register-iban.component.html',
  styleUrls: ['../../page-shared.css', './de-register-iban.component.scss'],
})
export class DeRegisterIbanComponent
  extends RouteComponent
  implements OnInit, OnDestroy
{
  paymentMethodsVM: PaymentMethodVM[] = null;
  navigationInProgress = false;
  selectorItems: SelectorItem[] = null;
  selectedItem: SelectorItem = null;
  noAvailablePaymentMethods = false;
  color = '#DB305E';
  market: Market = null;
  currentLoanTermsStr: string;
  modalActive: boolean = false;

  directDebitConsentData =
    "Wenn Sie auf 'Bestätigen' klicken, ermächtigen Sie Frink AB (Zaver), Anweisungen an Ihre Bank zu senden, um Ihr Konto gemäß dem Sepa-Lastschriftmandat zu belasten. Bedingungen ansehen";
  loading = true;
  showError = false;
  form: FormGroup;

  isIbanVerified: boolean = false;
  verifiedBankName: string | null = null;
  verifiedBankIconUrl: string | null = null;

  disableForm: boolean = false;
  pollingSubscription: Subscription;
  pollingInProgress: boolean = false;

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected stateService: StateService,
    protected headerService: HeaderService,
    private userService: UserService,
    private valuePipe: ValuePipe,
    private loanOptionService: DeLoanOptionService
  ) {
    super(router, route, stateService, headerService);
  }

  ngOnInit(): void {
    this.form = new FormGroup({
      iban: new FormControl('', [Validators.required]),
    });

    // handle real-time IBAN validation
    this.form.controls.iban.valueChanges
      .pipe(
        // Only trigger when input is 12+ characters
        filter((value) => value && value.length >= 12),
        // Wait for 500ms of inactivity before sending a request
        debounceTime(500),
        // Don't send a request if the value hasn't changed
        distinctUntilChanged(),
        // Cancel previous pending requests and switch to the new one
        switchMap((iban) => this.userService.verifyIban(iban))
      )
      .subscribe(
        (response) => {
          if (response) {
            this.isIbanVerified = response.verified; // Will be true only when the full IBAN is typed in
            this.verifiedBankName = response.bankName;
            this.verifiedBankIconUrl = response.bankIconUrl;
          } else {
            this.isIbanVerified = false;
            this.verifiedBankName = null;
          }
        },
        (error) => {
          this.isIbanVerified = false;
          this.verifiedBankName = null;
          this.handleError(error);
        }
      );

    this.prepareModalInformation();
    this.loading = false;
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  toggleModal(): void {
    this.modalActive = !this.modalActive;
  }

  onTermsClick() {
    this.openModalWithTerms();
  }

  prepareModalInformation(): void {
    this.selectorItems = [
      {
        tab: 'Ihr Kauf',
        title: 'Zusammenfassung',
        lineItems: this.state.paymentInformation?.lineItems,
        totalValue: this.stateService.getCustomTaxTypeText(
          `Gesamtbetrag inkl. MwSt: ${this.valuePipe.transform(
            this.state.totalCheckoutValue
          )}`,
          this.state.checkoutCustomizations
        ),
      },
    ];

    if (
      this.state.loanOptions &&
      this.state.loanOptions.length > 0 &&
      this.state.loanOptions[0]?.termId
    ) {
      const selectedLoanOption = this.state.loanOptions.find(
        (loanOption: LoanOption) => loanOption.termId
      );

      if (selectedLoanOption) {
        this.currentLoanTermsStr = this.loanOptionService.buildLoanTermsString(
          selectedLoanOption,
          this.state.totalCheckoutValue
        );

        this.selectorItems = [
          ...this.selectorItems,
          {
            tab: 'Zahlungsbedingungen',
            title: 'Bedingungen für den Kauf auf Raten',
            descriptions: [{ text: this.currentLoanTermsStr }],
            links: [
              {
                text: 'Bedingungen für Teilzahlungen',
                onClick: () =>
                  this.openURLinNewWindow(selectedLoanOption.loanTermsURL),
              },
              {
                ...(this.state.checkoutCustomizations.merchantTermsUrl !==
                  null &&
                  this.state.checkoutCustomizations.merchantName !== null && {
                    text: this.stateService.getCustomTermsUrlText(
                      this.state.checkoutCustomizations.merchantName + ' AGB',
                      this.state.checkoutCustomizations
                    ),
                    onClick: () =>
                      this.openURLinNewWindow(
                        this.state.checkoutCustomizations.merchantTermsUrl
                      ),
                  }),
              },
              {
                ...(this.state.market === Market.AT && {
                  text: 'Europäische Standardinformationen für Kreditierungen nach dem Verbraucherkreditgesetz',
                  onClick: () =>
                    this.openURLinNewWindow(selectedLoanOption.secciURL),
                }),
              },
            ],
          },
        ];
      }
    }
  }

  openURLinNewWindow(url: string): void {
    window.open(url, '_blank');
  }

  deactivateModal(): void {
    this.modalActive = false;
  }

  openModalWithTerms(): void {
    this.selectorItems = [
      {
        tab: 'SEPA',
        title: 'SEPA-Lastschrift-Mandat',
        descriptions: [
          {
            text: 'Ich ermächtige (a) Frink AB (Zaver), Gläubiger-Identifikationsnummer DE34ZZZ00002650270,  Zahlungen von meinem Konto mittels Lastschrift einzuziehen. Zugleich (b) weise ich mein Kreditinstitut an, die von Zaver auf mein Konto gezogenen Lastschriften einzulösen. Ich kann innerhalb von acht Wochen, beginnend mit dem Belastungsdatum, die Erstattung des belasteten Betrages verlangen. Es gelten dabei die mit meinem Kreditinstitut vereinbarten Bedingungen.',
          },
        ],
      },
    ];

    this.selectedItem = this.selectorItems[this.selectorItems.length - 1];
    this.modalActive = true;
  }

  async initiateAIS() {
    try {
      await this.userService.setupAISForIbanRegistration();
      await this.navigate();
    } catch (error) {
      this.handleError(error);
    }
  }

  public getButtonText(): string {
    return this.disableForm ? 'Weiter zu Ihrer Bank' : 'Weiter';
  }

  async handleCtaClick(): Promise<void> {
    this.navigationInProgress = true;
    if (this.disableForm) {
      try {
        await this.navigate();
      } catch (err) {
        this.handleError(err);
      }
    } else {
      if (this.form.valid) {
        try {
          const formattedIbanNumber = this.form.controls.iban.value
            .trim()
            .replace(/\s/g, '');

          await this.pollForRegisterIban(formattedIbanNumber);
        } catch (err) {
          this.handleError(err);
        }
      } else {
        this.showError = true;
      }
    }
    this.navigationInProgress = false;
  }

  async pollForRegisterIban(formattedIbanNumber: string): Promise<void> {
    this.pollingInProgress = true;
    this.pollingSubscription = this.userService
      .registerManualIbanInput(formattedIbanNumber, this.directDebitConsentData)
      .pipe(
        repeatWhen((obs) => obs.pipe(delay(1000))),
        take(120)
      )
      .subscribe((response: StandardCheckoutResponse) => {
        if (response.rejected) {
          this.disableForm = true;
          this.form.controls.iban.disable();
          this.navigationInProgress = false;
          this.pollingInProgress = false;
          this.stopPolling();
        } else if (response.success || response.error) {
          this.stopPolling();
          this.navigate();
        }
      });
  }

  stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }
}
